import App from '@/App.vue';
import router from './router';
import '@boqnet/ui/typography.css';
import '@/assets/css/index.css';
import { createApp } from 'vue';
import { AxiosError } from 'axios';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';
import i18n from './plugins/vue-i18n';
import PrimeVue from 'primevue/config';
import { install } from 'vue3-recaptcha-v2';
import PrimeVuePresets from '@/presets/primevue';
import { validateIsAxiosError } from '@/utils/axios';
import { usePusherStore } from '@/stores/pusher.store';
import { ErrorResponse } from '@/types/api/response.types';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { useUserPreferencesStore } from '@/stores/user-preferences.store';
import { analyticsService } from './plugins/post-hog';
import { LDPlugin } from 'launchdarkly-vue-client-sdk';
import { getLDConfig } from '@/plugins/launch-darkly';
import { initPermissionsWatcher } from '@/utils/casl.utils';
import { scrubBreadcrumb } from '@/common/utils/sentry-breadcrumbs.utils';

const appVersion = __APP_VERSION__ || 'development';
const sentryRelease =
  (import.meta.env.VITE_APP_SENTRY_RELEASE as string | undefined) || `BOQNetClient@${appVersion}`;

const app = createApp(App, { name: sentryRelease });
const pinia = createPinia();

pinia.use(piniaPluginPersistedstate);
app.use(pinia);

// Sync CASL permissions whenever the logged-in user, active org, project or
// site changes. Must run after app.use(pinia) so the store is accessible.
initPermissionsWatcher();

usePusherStore().connect();

const RETRY_ERROR_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CANCELED']);

interface ExtendedError extends Error {
  config?: {
    url?: string;
    method?: string;
  };
  status?: number;
  isAxiosError?: boolean;
  response?: {
    status?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
  };
}

if (import.meta.env.VITE_APP_SENTRY_DSN as string) {
  Sentry.init({
    app: app,
    release: sentryRelease,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN as string,
    environment: (import.meta.env.VITE_APP_SENTRY_ENV as string) ?? 'local',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracePropagationTargets: [import.meta.env.VITE_APP_API_BASE_URL, /^\/api\//],
    tracesSampleRate: Number.parseFloat(
      (import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE as string) ?? 0,
    ),
    replaysSessionSampleRate: Number.parseFloat(
      (import.meta.env.VITE_APP_SENTRY_TRACES_REPLAYS_SESSION_SAMPLE_RATE as string) ?? 0,
    ),
    replaysOnErrorSampleRate: Number.parseFloat(
      (import.meta.env.VITE_APP_SENTRY_TRACES_REPLAYS_ON_ERROR_SAMPLE_RATE as string) ?? 0,
    ),
    attachStacktrace: false,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend(event, hint) {
      const error = hint?.originalException as ExtendedError;

      // TODO: Remove when no longer using Primevue input fields
      // Filter out PrimeVue focus trap errors
      if (
        error?.message?.includes('nextSibling') ||
        error?.stack?.includes('findNextFocusableElement')
      ) {
        return null;
      }

      if (error) {
        const isAxiosError = validateIsAxiosError(error);

        if (isAxiosError) {
          const axiosError = error as AxiosError;

          if (axiosError.response) {
            const url = axiosError.config?.url ?? 'unknown URL';
            const status = axiosError.response.status;
            const code = axiosError.code ?? 'UNKNOWN_ERROR';
            const method = axiosError.config?.method?.toUpperCase() ?? 'UNKNOWN';

            // Special handling for image/logo 403 errors
            if (
              status === 403 &&
              (url.includes('/images') || url.includes('/logos')) &&
              method !== 'DELETE'
            ) {
              event.message = 'Image upload failed with status code 403';

              if (event.exception?.values?.[0]) {
                event.exception.values[0].value = 'Image upload failed with status code 403';
              }
            }
            // Filter out EXPECTED client errors
            else if (status === 401) {
              // Authentication errors are common
              return null;
            } else if (status === 409) {
              // Version conflicts are handled via the conflict dialog; logged separately as info
              return null;
            } else if (status === 404 && method === 'GET') {
              // 404 on GET requests (missing resources)
              return null;
            } else if (
              (status === 400 || status === 422) &&
              (url.includes('/register') || url.includes('/auth'))
            ) {
              // Filter validation errors for auth/registration endpoints
              return null;
            } else if (code && RETRY_ERROR_CODES.has(code)) {
              // Retryable errors are not logged to Sentry
              return null;
            } else {
              // Format all other errors with clear method and URL info
              let errorMessage = `${method} ${url} failed with status ${status}`;

              // Include error details from the API response
              if (axiosError.response?.data) {
                const responseData = axiosError.response.data as ErrorResponse | undefined;
                if (responseData?.errors && responseData.errors.length > 0) {
                  const errorDetails = responseData.errors
                    .map((err) => err.displayMessage || err.errorType)
                    .filter(Boolean)
                    .join('; ');
                  if (errorDetails) {
                    errorMessage += ` - ${errorDetails}`;
                  }
                } else if (responseData?.meta?.message) {
                  errorMessage += ` - ${responseData.meta.message}`;
                }
              }

              if (event.exception?.values?.[0]) {
                event.exception.values[0].value = errorMessage;
              }
            }
          }
        }
      }

      return event;
    },
  });
}

// TODO: Remove when no longer using Primevue input fields
// Suppress console.error for PrimeVue focus trap errors
const originalError = console.error;
console.error = (...args) => {
  const errorMessage = args[0]?.toString() || '';

  // Suppress the specific nextSibling error from PrimeVue focus trap
  if (errorMessage.includes('nextSibling') || errorMessage.includes('findNextFocusableElement')) {
    return;
  }

  // Log all other errors normally
  originalError.apply(console, args);
};

// Global error handler for uncaught errors
globalThis.addEventListener('error', (event) => {
  const error = event.error;

  // Suppress PrimeVue focus trap errors
  if (
    error?.message?.includes('nextSibling') ||
    error?.stack?.includes('findNextFocusableElement') ||
    event.message?.includes('nextSibling')
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Global unhandled promise rejection handler
globalThis.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  // Suppress PrimeVue focus trap errors
  if (
    error?.message?.includes('nextSibling') ||
    error?.stack?.includes('findNextFocusableElement')
  ) {
    event.preventDefault();
    return false;
  }
});

// Initialize LaunchDarkly feature flags plugin
const ldConfig = await getLDConfig();
app.use(LDPlugin, {
  ...ldConfig,
  context: {
    kind: 'user',
    key: 'BOQNetInternalUser',
  },
});

app.use(router);
app.use(i18n);
app.use(PrimeVue, {
  unstyled: true,
  pt: PrimeVuePresets,
});
app.use(install, {
  sitekey: import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string,
});
analyticsService.init();

const userPrefStore = useUserPreferencesStore();
userPrefStore.loadFromLocalStorage();

app.mount('#app');

export default app;
